import React, { useState, useMemo, useCallback } from 'react';
import { useData } from '../../hooks/useData';
import { useApp } from '../../contexts/AppContext';
import { TABLES } from '../../constants/supabaseConstants';
import { MODAL_TYPES } from '../../constants/modalTypes';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../utils/errorLogging';
import { logger } from '../../utils/logger';
import { NotePencil, Trash, Plus, DownloadSimple, Check, X } from '@phosphor-icons/react';
import pageStyles from '../../styles/pages.module.css';
import styles from './BOQNotesTab.module.css';

/**
 * BOQNotesTab — Manages per-project BOQ notes.
 * Notes are grouped by level-0 BB chapters, with a "כללי" group for uncategorized.
 * Users can toggle, edit, add, delete notes, and import from system/architect templates.
 */
const BOQNotesTab = ({ bbChapters, projectId }) => {
  const { openModal, showSuccess, showError } = useApp();
  const { logError } = useErrorLogging();

  // ── Local state ──
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [addingChapterId, setAddingChapterId] = useState(null); // chapter id or '__general__'
  const [newNoteText, setNewNoteText] = useState('');

  // ── Fetch project notes ──
  const {
    data: projectNotes,
    isLoading,
    error,
    insert: insertNote,
    update: updateNote,
    remove: removeNote,
  } = useData(TABLES.PROJECT_BOQ_NOTES, {
    select: '*',
    filter: { project_id: projectId },
    enabled: Boolean(projectId),
    realtime: true,
    onError: err => {
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Failed to fetch project BOQ notes',
      });
    },
  });

  // ── Build level-0 chapter lookup for grouping ──
  const level0Chapters = useMemo(() => {
    if (!bbChapters) return [];
    return bbChapters
      .filter(ch => ch.depth === 0)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [bbChapters]);

  // ── Group notes by bb_chapter_id ──
  const groupedNotes = useMemo(() => {
    if (!projectNotes) return [];

    // Build a map: chapter_id -> notes[]
    const map = new Map();
    // Always include a "כללי" group
    map.set(null, []);

    // Pre-populate groups for chapters that have notes
    projectNotes.forEach(note => {
      const key = note.bb_chapter_id || null;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(note);
    });

    // Sort notes within each group by display_order
    map.forEach(notes => notes.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)));

    // Build ordered groups: כללי first, then by chapter display_order
    const groups = [];

    // כללי group (notes with bb_chapter_id = null)
    const generalNotes = map.get(null) || [];
    groups.push({ id: null, name: 'כללי', code: null, notes: generalNotes });

    // Chapter groups
    level0Chapters.forEach(ch => {
      const notes = map.get(ch.id) || [];
      if (notes.length > 0) {
        groups.push({ id: ch.id, name: ch.name_he, code: ch.code, notes });
      }
    });

    // Any notes with chapter IDs not in level-0 (edge case)
    map.forEach((notes, key) => {
      if (key === null) return;
      if (level0Chapters.some(ch => ch.id === key)) return;
      if (notes.length > 0) {
        groups.push({ id: key, name: 'אחר', code: null, notes });
      }
    });

    return groups;
  }, [projectNotes, level0Chapters]);

  // ── Total enabled count for display ──
  const enabledCount = useMemo(() => {
    if (!projectNotes) return 0;
    return projectNotes.filter(n => n.enabled).length;
  }, [projectNotes]);

  // ── Handlers ──

  // Toggle note enabled/disabled
  const handleToggle = useCallback(
    async note => {
      try {
        await updateNote({
          id: note.id,
          data: { enabled: !note.enabled, updated_at: new Date().toISOString() },
        });
      } catch (err) {
        logger.error('Failed to toggle note', err);
        showError('שגיאה בעדכון הערה');
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_MUTATION,
          message: 'Failed to toggle BOQ note',
        });
      }
    },
    [updateNote, showError, logError],
  );

  // Start editing a note
  const handleStartEdit = useCallback(note => {
    setEditingNoteId(note.id);
    setEditText(note.text);
  }, []);

  // Save edited note
  const handleSaveEdit = useCallback(async () => {
    if (!editingNoteId || !editText.trim()) return;
    try {
      await updateNote({
        id: editingNoteId,
        data: { text: editText.trim(), updated_at: new Date().toISOString() },
      });
      setEditingNoteId(null);
      setEditText('');
      showSuccess('ההערה עודכנה');
    } catch (err) {
      logger.error('Failed to update note', err);
      showError('שגיאה בעדכון הערה');
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to update BOQ note',
      });
    }
  }, [editingNoteId, editText, updateNote, showSuccess, showError, logError]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditText('');
  }, []);

  // Delete a note (with confirmation)
  const handleDelete = useCallback(
    async noteId => {
      if (!window.confirm('למחוק הערה זו?')) return;
      try {
        await removeNote(noteId);
        showSuccess('ההערה נמחקה');
      } catch (err) {
        logger.error('Failed to delete note', err);
        showError('שגיאה במחיקת הערה');
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_MUTATION,
          message: 'Failed to delete BOQ note',
        });
      }
    },
    [removeNote, showSuccess, showError, logError],
  );

  // Add a new custom note
  const handleAddNote = useCallback(
    async chapterId => {
      if (!newNoteText.trim()) return;
      try {
        // Compute next display_order for this chapter
        const chapterNotes = (projectNotes || []).filter(n =>
          chapterId === '__general__' ? !n.bb_chapter_id : n.bb_chapter_id === chapterId,
        );
        const maxOrder = chapterNotes.reduce((max, n) => Math.max(max, n.display_order ?? 0), 0);

        await insertNote({
          project_id: projectId,
          text: newNoteText.trim(),
          bb_chapter_id: chapterId === '__general__' ? null : chapterId,
          display_order: maxOrder + 1,
          enabled: true,
          source_type: 'custom',
        });
        setNewNoteText('');
        setAddingChapterId(null);
        showSuccess('הערה נוספה');
      } catch (err) {
        logger.error('Failed to add note', err);
        showError('שגיאה בהוספת הערה');
        logError(err, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_MUTATION,
          message: 'Failed to add BOQ note',
        });
      }
    },
    [newNoteText, projectNotes, projectId, insertNote, showSuccess, showError, logError],
  );

  // Open import modal via centralized ModalManager
  const handleOpenImport = useCallback(() => {
    openModal(MODAL_TYPES.IMPORT_BOQ_NOTES, {
      projectId,
      level0Chapters,
      existingNotes: projectNotes || [],
    });
  }, [openModal, projectId, level0Chapters, projectNotes]);

  // ── Render ──

  if (isLoading) {
    return <div className={pageStyles.loadingContainer}>טוען הערות...</div>;
  }

  if (error) {
    return <div className={pageStyles.emptyState}>שגיאה בטעינת הערות: {error.message}</div>;
  }

  const hasNotes = projectNotes && projectNotes.length > 0;

  return (
    <div>
      {/* Actions bar */}
      <div className={styles.actionsBar}>
        <div className={styles.actionsRight}>
          <button className={pageStyles.primaryButton} onClick={handleOpenImport}>
            <DownloadSimple size={18} weight="bold" />
            ייבא הערות
          </button>
          <button
            className={pageStyles.secondaryButton}
            onClick={() => setAddingChapterId('__general__')}
          >
            <Plus size={18} weight="bold" />
            הוסף הערה
          </button>
        </div>
        {hasNotes && (
          <div className={styles.actionsLeft}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {enabledCount} מתוך {projectNotes.length} הערות פעילות
            </span>
          </div>
        )}
      </div>

      {/* Add note inline (when adding to כללי / no specific chapter) */}
      {addingChapterId === '__general__' && (
        <div className={styles.chapterGroup}>
          <div className={styles.addNoteRow}>
            <input
              className={styles.addNoteInput}
              value={newNoteText}
              onChange={e => setNewNoteText(e.target.value)}
              placeholder="הקלד הערה חדשה..."
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddNote('__general__');
                }
                if (e.key === 'Escape') {
                  setAddingChapterId(null);
                  setNewNoteText('');
                }
              }}
            />
            {/* Chapter selector for the new note */}
            <select
              className={styles.chapterSelect}
              value={addingChapterId}
              onChange={e => setAddingChapterId(e.target.value)}
            >
              <option value="__general__">כללי</option>
              {level0Chapters.map(ch => (
                <option key={ch.id} value={ch.id}>
                  {ch.code} - {ch.name_he}
                </option>
              ))}
            </select>
            <button
              className={pageStyles.primaryButton}
              onClick={() => handleAddNote(addingChapterId)}
              disabled={!newNoteText.trim()}
              style={{ padding: '8px 16px', minHeight: 36 }}
            >
              <Check size={16} weight="bold" />
            </button>
            <button
              className={pageStyles.secondaryButton}
              onClick={() => {
                setAddingChapterId(null);
                setNewNoteText('');
              }}
              style={{ padding: '8px 16px', minHeight: 36 }}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!hasNotes && !addingChapterId && (
        <div className={styles.emptyNotes}>
          <NotePencil size={48} weight="light" className={styles.emptyIcon} />
          <div className={styles.emptyTitle}>אין הערות עדיין</div>
          <div className={styles.emptyDescription}>
            ייבא הערות מתבניות מערכת או הוסף הערות מותאמות אישית לפרויקט
          </div>
          <button className={pageStyles.primaryButton} onClick={handleOpenImport}>
            <DownloadSimple size={18} weight="bold" />
            ייבא הערות
          </button>
        </div>
      )}

      {/* Notes grouped by chapter */}
      {groupedNotes.map(group => {
        if (group.notes.length === 0 && group.id !== null) return null;
        if (group.notes.length === 0 && group.id === null && !hasNotes) return null;
        if (group.notes.length === 0) return null;

        return (
          <div key={group.id || '__general__'} className={styles.chapterGroup}>
            <div className={styles.chapterHeader}>
              <span>{group.name}</span>
              {group.code && <span className={styles.chapterCode}>({group.code})</span>}
            </div>
            {group.notes.map(note => (
              <div key={note.id} className={note.enabled ? styles.noteRow : styles.noteRowDisabled}>
                {/* Toggle */}
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={note.enabled}
                    onChange={() => handleToggle(note)}
                  />
                  <span className={styles.toggleSlider} />
                </label>

                {/* Note text — editable or display */}
                <div className={styles.noteText}>
                  {editingNoteId === note.id ? (
                    <textarea
                      className={styles.noteTextEdit}
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSaveEdit();
                        }
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                    />
                  ) : (
                    <div
                      className={styles.noteTextDisplay}
                      onClick={() => handleStartEdit(note)}
                      title="לחץ לעריכה"
                    >
                      {note.text}
                    </div>
                  )}
                </div>

                {/* Source badge */}
                {note.source_type === 'system' && (
                  <span className={styles.sourceBadgeSystem}>מערכת</span>
                )}
                {note.source_type === 'architect' && (
                  <span className={styles.sourceBadgeArchitect}>אדריכל</span>
                )}

                {/* Actions */}
                <div className={styles.noteActions}>
                  {editingNoteId === note.id ? (
                    <>
                      <button className={styles.iconButton} onClick={handleSaveEdit} title="שמור">
                        <Check size={18} weight="bold" />
                      </button>
                      <button className={styles.iconButton} onClick={handleCancelEdit} title="בטל">
                        <X size={18} weight="bold" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className={styles.iconButton}
                        onClick={() => handleStartEdit(note)}
                        title="ערוך"
                      >
                        <NotePencil size={18} />
                      </button>
                      <button
                        className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                        onClick={() => handleDelete(note.id)}
                        title="מחק"
                      >
                        <Trash size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default BOQNotesTab;
